import type { UTCDate } from '@date-fns/utc';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { useMemo } from 'react';

import {
  COMPOSITION_GET_DATA_V3,
  COMPOSITION_HEADCOUNT_ONLY_V3,
  type CompositionHeadcountOnlyV3Query,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
  type CompositionV3Ent,
  type CompositionV3Filters,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useUserPreferences } from '../../../../../../shared/user-preferences/hooks/useUserPreferences';
import { getPreferenceScope } from '../../../../../../shared/user-preferences/utils/preferences';
import { getDateRangeFromPreset } from '../../../../filters/constants/datePresets';
import {
  useFilterContext,
  useIsTaxonomyResolved,
} from '../../../../filters/contexts/FilterProvider';
import { useAvailableDateRangeWithTrialLag } from '../../../../filters/hooks/useAvailableDateRangeWithTrialLag';
import { useEffectiveFilters } from '../../../../filters/hooks/useEffectiveFilters';
import {
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type PrimaryFilterId,
  type StaticListFilter,
} from '../../../../filters/types/filters';
import {
  getBaseCompositionV3BreakdownLevels,
  getCompositionV3BreakdownOptions,
} from '../../../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import {
  getCompositionV3Variables,
  getCustomSkillDeepestLevelFromFilters,
  normalizeCompositionEffectiveFilters,
} from '../../../../filters/utils/composition-v3/getCompositionV3Variables';
import { isCompositionV3CustomSkillTaxonomyReady } from '../../../../filters/utils/composition-v3/is-composition-v3-custom-skill-taxonomy-ready';
import {
  getCompositionV3MonthRefs,
  getMonthRefsFromCompositionV3Filters,
} from '../../../../filters/utils/composition-v3/month-refs-from-v3-filters';
import { getFormattedDateString } from '../../../../filters/utils/dateUtils';
import {
  effectiveFiltersWithSinglePrimaryEntity,
  useChartPrimaryFilter,
} from '../../../../hooks/useChartPrimaryFilter';
import { ChartLoader } from '../../../Chart/ChartLoader';
import { useChart } from '../../../Chart/ChartProvider';
import { useTabContentConfig } from '../../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../../PageConfigProvider/types';
import { isCompositionSecondaryDimension } from '../../../PageConfigProvider/types/dimension';
import type { PositionedItem } from '../../../PageConfigProvider/types/item';
import { usePageState } from '../../../PageState/PageStateProvider';
import { haveSelectionsBeenModified } from '../../../PageState/types';
import type { DataProviderChildren } from '../../types';
import { useUrqlQueryWithStatus } from '../../utils/data-provider-results-utils';
import {
  buildExhaustiveBreakdownDimensionsKey,
  useExhaustiveBreakdownIdsV3,
} from './useExhaustiveBreakdownIdsV3';

function resolveCompositionV3RequestFilters({
  filters,
  isTimeseries,
  revelioPageId,
  dateRangeDefault,
  availableDateRange,
}: {
  filters: CompositionV3Filters;
  isTimeseries: boolean;
  revelioPageId?: RevelioPageId;
  dateRangeDefault?: string;
  availableDateRange?: {
    month?: { min?: UTCDate | null; max?: UTCDate | null };
  } | null;
}): CompositionV3Filters {
  const { start_date, end_date } = filters;
  const hasMultiMonthRange =
    Boolean(start_date) && Boolean(end_date) && start_date !== end_date;

  if (hasMultiMonthRange) {
    return filters;
  }

  if (
    revelioPageId === RevelioPageId.COMPANY_OVERVIEW_V2 &&
    isTimeseries &&
    dateRangeDefault &&
    availableDateRange?.month?.min &&
    availableDateRange?.month?.max
  ) {
    const { start, end } = getDateRangeFromPreset(
      dateRangeDefault,
      availableDateRange.month.max,
      availableDateRange.month.min
    );
    return {
      ...filters,
      start_date: getFormattedDateString(start, 'month'),
      end_date: getFormattedDateString(end, 'month'),
    };
  }

  const systemMax = availableDateRange?.month?.max;
  const systemMin = availableDateRange?.month?.min;
  const resolvedEnd =
    end_date ?? (systemMax ? getFormattedDateString(systemMax, 'month') : '');
  const resolvedStart =
    start_date ?? (systemMin ? getFormattedDateString(systemMin, 'month') : '');

  if (resolvedStart === '' || resolvedEnd === '') {
    return filters;
  }

  return {
    ...filters,
    start_date: resolvedStart,
    end_date: resolvedEnd,
  };
}

function useCompositionV3Setup() {
  const { filterState, activeFilterMap, geographyMode } = useFilterContext();
  const { config, revelioPageId } = useTabContentConfig();
  const preferenceScope = getPreferenceScope({
    pageId: revelioPageId,
    primaryFilterId: filterState.activeKey ?? 'NO_PRIMARY_FILTER',
  });
  const { isInitialLoading: isPreferencesInitialLoading } =
    useUserPreferences(preferenceScope);
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const {
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  } = useCustomSkillTaxonomyTree();

  return {
    filterState,
    activeFilterMap,
    config,
    revelioPageId,
    preferenceScope,
    isPreferencesInitialLoading,
    effectiveFilters,
    geographyMode,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  };
}

type CompositionV3QueryResult =
  | CompositionV3DataQuery
  | CompositionHeadcountOnlyV3Query;

type CompositionV3DataProviderProps = {
  primaryDimension: PrimaryFilterId;
  isTimeseries?: boolean;
  entitiesOverride?: CompositionV3Ent[];
  getSelectionBreakdowns?: boolean;
  returnAllSkills?: boolean;
  requireFullMetrics?: boolean;
  children: DataProviderChildren<CompositionV3QueryResult | undefined>;
};

export const CompositionV3RawDataProvider = ({
  primaryDimension,
  isTimeseries = false,
  entitiesOverride,
  getSelectionBreakdowns,
  returnAllSkills: _returnAllSkills,
  requireFullMetrics = false,
  children,
}: CompositionV3DataProviderProps) => {
  if (getSelectionBreakdowns) {
    return (
      <CompositionV3BreakdownDataProvider
        primaryDimension={primaryDimension}
        isTimeseries={isTimeseries}
        entitiesOverride={entitiesOverride}
        // biome-ignore lint/correctness/noChildrenProp: migrated from develop
        children={children}
      />
    );
  }

  return (
    <CompositionV3NonBreakdownDataProvider
      primaryDimension={primaryDimension}
      isTimeseries={isTimeseries}
      entitiesOverride={entitiesOverride}
      requireFullMetrics={requireFullMetrics}
      // biome-ignore lint/correctness/noChildrenProp: migrated from develop
      children={children}
    />
  );
};

const CompositionV3NonBreakdownDataProvider = ({
  primaryDimension,
  isTimeseries = false,
  entitiesOverride,
  requireFullMetrics = false,
  children,
}: CompositionV3DataProviderProps) => {
  const setupContext = useCompositionV3Setup();
  const {
    revelioPageId,
    config,
    activeFilterMap,
    isPreferencesInitialLoading,
    effectiveFilters,
    geographyMode,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  } = setupContext;
  const correctionPreferencesSynced =
    areCorrectionPreferenceFiltersPopulated(activeFilterMap);
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();

  const customSkillTaxonomyReady = isCompositionV3CustomSkillTaxonomyReady({
    effectiveFilters,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  });

  const useHeadcountOnly =
    !requireFullMetrics && revelioPageId === RevelioPageId.COMPANY_OVERVIEW_V2;

  const query = useHeadcountOnly
    ? COMPOSITION_HEADCOUNT_ONLY_V3
    : COMPOSITION_GET_DATA_V3;

  const breakdownOptionsOverride = useHeadcountOnly
    ? getBaseCompositionV3BreakdownLevels(geographyMode)
    : undefined;

  const baseVariables = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot: !isTimeseries,
    geographyMode,
    getSelectionBreakdowns: false,
    customSkillTree,
    compositionEntityScopes: entitiesOverride,
    breakdownOptionsOverride,
    activeFilterMap,
  });

  const variables: CompositionV3DataQueryVariables = {
    ...baseVariables,
    filters: resolveCompositionV3RequestFilters({
      filters: baseVariables.filters,
      isTimeseries,
      revelioPageId,
      dateRangeDefault: config.dateRangeDefault,
      availableDateRange,
    }),
  };

  const monthRefs = getCompositionV3MonthRefs({
    availableDateRange,
    filters: variables.filters,
  });

  if (
    !customSkillTaxonomyReady ||
    !isCompositionV3QueryVariablesReady(variables) ||
    isPreferencesInitialLoading ||
    !correctionPreferencesSynced
  ) {
    return <ChartLoader />;
  }

  return (
    <CompositionV3Query
      query={query}
      variables={variables}
      monthRefs={monthRefs}
    >
      {children}
    </CompositionV3Query>
  );
};

const CompositionV3BreakdownDataProvider = ({
  primaryDimension,
  isTimeseries = false,
  entitiesOverride,
  children,
}: CompositionV3DataProviderProps) => {
  const setupContext = useCompositionV3Setup();
  const {
    config,
    activeFilterMap,
    revelioPageId: pageId,
    isPreferencesInitialLoading,
    effectiveFilters,
    geographyMode,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  } = setupContext;
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const chartPrimaryFilter = useChartPrimaryFilter({ primaryDimension });
  const { item } = useChart();
  const effectiveFiltersWithSingleEntity =
    effectiveFiltersWithSinglePrimaryEntity({
      item,
      effectiveFilters,
      chartPrimaryFilter,
    });
  const { pageState } = usePageState();
  const isModified = item?.id
    ? haveSelectionsBeenModified(pageState, item.id)
    : false;

  const configItems = config.items ?? [];

  const shareOfSegmentPlotsNeedingSubfilters = configItems.filter(
    (chartItem) => {
      if (!plotUsesShareOfSegmentPageSelections(chartItem)) return false;
      const key = canonicalizeBreakdownKey(chartItem.plot?.secondaryDimension);
      return (
        key !== null &&
        (SUPPORTED_BREAKDOWN_KEYS.has(key) || key === STATE_OVER_MSA_KEY)
      );
    }
  );

  const subFilterSelectionsByPlotId = (() => {
    const base: Record<string, SelectionListEntry[] | undefined> = {
      ...(isModified
        ? {
            ...pageState.initialSelections,
            [item.id]: pageState.selections?.[item?.id],
          }
        : pageState.initialSelections),
    };
    shareOfSegmentPlotsNeedingSubfilters.forEach((p) => {
      if (base[p.id] === undefined) {
        base[p.id] = [];
      }
    });
    return base;
  })();

  const allBreakdownSelectionsReady = areAllBreakdownSelectionsReadyV3(
    configItems,
    subFilterSelectionsByPlotId
  );

  const baseVariables = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters: effectiveFiltersWithSingleEntity,
    getSelectionBreakdowns: false,
    isSnapshot: !isTimeseries,
    customSkillTree,
    geographyMode,
    compositionEntityScopes: entitiesOverride,
    activeFilterMap,
  });

  const normalizedEffectiveForBreakdown = normalizeCompositionEffectiveFilters(
    effectiveFiltersWithSingleEntity
  );

  const customSkillDeepestLevel = getCustomSkillDeepestLevelFromFilters(
    normalizedEffectiveForBreakdown
  );

  const breakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: baseVariables.filters,
    customSkillTree,
    customSkillDeepestLevel,
    geographyMode,
  });

  const exhaustiveBreakdownDimensionsKey = useMemo(
    () =>
      buildExhaustiveBreakdownDimensionsKey(
        configItems
          .filter(plotUsesShareOfSegmentPageSelections)
          .map((chartItem) => chartItem.plot?.secondaryDimension)
      ),
    [configItems]
  );

  const { exhaustiveBreakdownIds, isExhaustiveBreakdownIdsReady } =
    useExhaustiveBreakdownIdsV3({
      secondaryDimensionsKey: exhaustiveBreakdownDimensionsKey,
      compositionFilters: baseVariables.filters,
      geographyMode,
      customSkillTree,
      customSkillDeepestLevel,
      isCustomSkillTaxonomyPending,
    });

  const variables: CompositionV3DataQueryVariables = {
    ...baseVariables,
    filters: resolveCompositionV3RequestFilters({
      filters: baseVariables.filters,
      isTimeseries,
      revelioPageId: pageId,
      dateRangeDefault: config.dateRangeDefault,
      availableDateRange,
    }),
    breakdown_options: {
      ...breakdownOptions,
      ...(exhaustiveBreakdownIds ?? {}),
    },
  };

  const customSkillTaxonomyReady = isCompositionV3CustomSkillTaxonomyReady({
    effectiveFilters: effectiveFiltersWithSingleEntity,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  });
  const correctionPreferencesSynced =
    areCorrectionPreferenceFiltersPopulated(activeFilterMap);

  const entities = variables.entities;
  const entitiesEmpty = Array.isArray(entities) && entities.length === 0;

  const monthRefs = getCompositionV3MonthRefs({
    availableDateRange,
    filters: variables.filters,
  });

  if (
    !customSkillTaxonomyReady ||
    !isExhaustiveBreakdownIdsReady ||
    !allBreakdownSelectionsReady ||
    !isCompositionV3QueryVariablesReady(variables) ||
    isPreferencesInitialLoading ||
    !correctionPreferencesSynced ||
    entitiesEmpty
  ) {
    return <ChartLoader />;
  }

  return (
    <CompositionV3Query
      variables={variables}
      monthRefs={monthRefs}
      haveSelectionsBeenModified={isModified}
    >
      {children}
    </CompositionV3Query>
  );
};

function areCompositionV3FilterDatesReady(
  filters: CompositionV3DataQueryVariables['filters']
): boolean {
  const { start_date, end_date } = filters;
  if (
    start_date === null ||
    start_date === undefined ||
    end_date === null ||
    end_date === undefined
  ) {
    return false;
  }
  if (start_date === '' || end_date === '') {
    return false;
  }
  return true;
}

function areCompositionV3CorrectionsReady(
  corrections: CompositionV3DataQueryVariables['corrections']
): boolean {
  const { with_contingent_workers, with_weight, with_sample_weight } =
    corrections;
  return (
    typeof with_contingent_workers === 'boolean' &&
    typeof with_weight === 'boolean' &&
    typeof with_sample_weight === 'boolean'
  );
}

function isCompositionV3QueryVariablesReady(
  variables: CompositionV3DataQueryVariables
): boolean {
  return (
    areCompositionV3FilterDatesReady(variables.filters) &&
    areCompositionV3CorrectionsReady(variables.corrections)
  );
}

function areCorrectionPreferenceFiltersPopulated(
  activeFilterMap: FilterSelectionsMap
): boolean {
  const hasSelections = (id: FilterId): boolean => {
    const filter = activeFilterMap.get(id) as StaticListFilter | undefined;
    return (
      filter?.type === FilterType.STATIC_LIST && filter.selections.length > 0
    );
  };
  return (
    hasSelections(FilterId.DATA_ADJUSTMENTS) &&
    hasSelections(FilterId.CONTINGENT_WORKERS)
  );
}

const CompositionV3Query = ({
  query = COMPOSITION_GET_DATA_V3,
  variables,
  monthRefs: monthRefsProp,
  children,
  haveSelectionsBeenModified,
}: {
  query?: TypedDocumentNode<
    CompositionV3QueryResult,
    CompositionV3DataQueryVariables
  >;
  variables: CompositionV3DataQueryVariables;
  monthRefs?: string[];
  children: DataProviderChildren<CompositionV3QueryResult | undefined>;
  haveSelectionsBeenModified?: boolean;
}) => {
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const [urqlState] = useUrqlQueryWithStatus<CompositionV3QueryResult>({
    query,
    variables,
    pause:
      !isTaxonomyResolved || !isCompositionV3QueryVariablesReady(variables),
  });
  const { data, status, error } = urqlState;
  const monthRefs =
    monthRefsProp ?? getMonthRefsFromCompositionV3Filters(variables.filters);

  return children({
    data,
    status,
    error: error ?? undefined,
    haveSelectionsBeenModified,
    monthRefs,
  });
};

type SelectionListEntry = { id: string };

type SubFilterSelectionsByPlotId =
  | Record<string, SelectionListEntry[] | undefined>
  | undefined;

const SUPPORTED_BREAKDOWN_KEYS = new Set([
  'Geography',
  'Rics',
  'Role',
  'Skill',
]);
const SKILL_KEY = 'Skill';
const STATE_OVER_MSA_KEY = 'StateOverMsa';

const plotUsesShareOfSegmentPageSelections = (item: PositionedItem): boolean =>
  item.plot?.chartType === 'share-of-segment' &&
  item.plot.secondaryDimension !== undefined &&
  isCompositionSecondaryDimension(item.plot.secondaryDimension);

const areAllBreakdownSelectionsReadyV3 = (
  configItems: PositionedItem[],
  subFilterSelections: SubFilterSelectionsByPlotId
): boolean => {
  const plotsNeedingSelections = configItems.filter((item) => {
    if (!plotUsesShareOfSegmentPageSelections(item)) return false;
    const key = canonicalizeBreakdownKey(item.plot?.secondaryDimension);
    return (
      key !== null &&
      (SUPPORTED_BREAKDOWN_KEYS.has(key) || key === STATE_OVER_MSA_KEY)
    );
  });

  if (plotsNeedingSelections.length === 0) return true;

  return plotsNeedingSelections.every(
    (item) => subFilterSelections?.[item.id] !== undefined
  );
};

const canonicalizeBreakdownKey = (key?: string): string | null => {
  if (!key) {
    return null;
  }

  const normalizedKey = key.toLowerCase();
  if (normalizedKey === 'skills' || normalizedKey === 'skill') {
    return SKILL_KEY;
  }

  return key.charAt(0).toUpperCase() + key.slice(1);
};
